import { customerListQuerySchema } from '@chukua/common';
import { Select } from 'antd';
import { useCallback, useEffect } from 'react';
import { getCustomer, listCustomers } from '../customers.api';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';

const PAGE_SIZE = 20;

interface CustomerSearchSelectProps {
  id?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  /** For a filter, where "no customer" is a valid answer. */
  allowClear?: boolean;
}

/**
 * Remote-search customer picker: `GET /customers?q=`, opening on the first page. Customers can run past
 * the ~100-row one-shot-fetch pattern `TransferOwnershipModal` uses for owners, so this one searches
 * instead. When `value` is set (a job being reopened), it fetches that one customer to show its name
 * rather than its id.
 */
export function CustomerSearchSelect({ id, value, onChange, disabled, allowClear }: CustomerSearchSelectProps) {
  const fetchCustomers = useCallback(
    (q: string | undefined) =>
      listCustomers(customerListQuerySchema.parse({ q, pageSize: PAGE_SIZE })).then((page) =>
        page.items.map((customer) => ({ value: customer.id, label: customer.name })),
      ),
    [],
  );
  const { pin, selectProps } = useRemoteSearchOptions(fetchCustomers);

  useEffect(() => {
    if (!value) return;
    getCustomer(value).then((customer) => pin({ value: customer.id, label: customer.name }));
  }, [value, pin]);

  return (
    <Select
      {...selectProps}
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      allowClear={allowClear}
      placeholder="Select or search customers"
      notFoundContent={selectProps.loading ? 'Searching…' : 'No customers found'}
    />
  );
}
